import {
  ActionName,
  CategoryName,
  TControlName,
  TDatabaseId,
} from '@infotech-soe/metaschema';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { DatasetRelated } from '../../models/dataset/DatasetRelated';
import { DocLinksMode } from '../../models/docLinks/docLinks';
import { MultiStepFormSnapshot } from '../../models/form/form';
import { DictionaryOutputs } from '../../services/dictionary/IDictionaryService';
import UniqueId from '../../services/uid/UniqueId';

import {
  emptyAction,
  failureAction,
  pendingAction,
  successAction,
} from './actionHandler';

export const enum DictionaryType {
  CATEGORY,
  TREE,
}

export const enum DictionaryEffectType {
  EXECUTE_COMMAND = 'command',
  UPDATE_FORM = 'form',
  UPDATE_WITH_OUT_PARAMS = 'updateWithOutParams',
  PROCESS_ACTION = 'action',
  WORK_TABLE = 'workTable',
  WORK_HOURS = 'workHours',
  DO_NOTHING = 'nothing',
  REFRESH_ON_CLOSE = 'refreshOnClose',
  CALL_BACK = 'callBack',
  DOC_LINKS = 'docLinks',
}

export enum DictionaryCloseReason {
  COMMON,
  CANCEL,
}

interface AsHook {
  hook?: boolean;
}

interface WithRefresh {
  refresh?: MultiStepFormSnapshot;
}

export interface CommandDictionaryEffect extends AsHook {
  type: DictionaryEffectType.EXECUTE_COMMAND;
  dataset: UniqueId;
  command: string;
}

export interface FormUpdateDictionaryEffect {
  type: DictionaryEffectType.UPDATE_FORM;
  form: UniqueId;
  field: TControlName;
}

export interface FormUpdateWithOutParamsDictionaryEffect {
  type: DictionaryEffectType.UPDATE_WITH_OUT_PARAMS;
  form: UniqueId;
  field: TControlName;
}

export interface ProcessActionDictionaryEffect
  extends AsHook, WithRefresh, DatasetRelated {
  type: DictionaryEffectType.PROCESS_ACTION;
  action: ActionName;
  category: CategoryName;
}

export interface WorkTableDictionaryEffect {
  type: DictionaryEffectType.WORK_TABLE;
  selectedDays: number[];
  tableId: TDatabaseId;
}

export interface WorkHoursDictionaryEffect {
  type: DictionaryEffectType.WORK_HOURS;
  tableId: TDatabaseId;
}

export interface DoNothingDictionaryEffect {
  type: DictionaryEffectType.DO_NOTHING;
}

export interface CallBackDictionaryEffect {
  type: DictionaryEffectType.CALL_BACK;
  callback?: (value: any) => void;
}

export interface RefreshOnCloseDictionaryEffect
  extends Required<WithRefresh>, DatasetRelated {
  type: DictionaryEffectType.REFRESH_ON_CLOSE;
}

export interface DocLinksDictionaryEffect {
  type: DictionaryEffectType.DOC_LINKS;
  docId: TDatabaseId;
  categoryName: CategoryName;
  mode: DocLinksMode;
}

export type DictionaryEffect =
  | CommandDictionaryEffect
  | FormUpdateDictionaryEffect
  | ProcessActionDictionaryEffect
  | FormUpdateWithOutParamsDictionaryEffect
  | WorkTableDictionaryEffect
  | WorkHoursDictionaryEffect
  | DoNothingDictionaryEffect
  | CallBackDictionaryEffect
  | RefreshOnCloseDictionaryEffect
  | DocLinksDictionaryEffect;

export interface DictionaryTree {
  type: DictionaryType.TREE;
  id: UniqueId;
  effect: FormUpdateWithOutParamsDictionaryEffect;
}

export interface DictionaryCategory {
  type: DictionaryType.CATEGORY;
  id: UniqueId;
  title: string;
  effect: DictionaryEffect;
  multiSelect: boolean;
  hideTree?: boolean;
  fullHierarchy?: boolean;
  hideCheckbox?: boolean;
}

export interface DictionaryEntity {
  readonly id: UniqueId;
  readonly title?: string;
  readonly effect: DictionaryEffect;
  readonly outputs: DictionaryOutputs | null;
  readonly multiSelect?: boolean;
  readonly hideTree?: boolean;
  readonly fullHierarchy?: boolean;
  readonly type: DictionaryType;
}

export interface DictionaryState {
  readonly all: Record<UniqueId, DictionaryEntity>;
  readonly order: UniqueId[];
}

const initialState: DictionaryState = {
  all: {},
  order: [],
};

const dictionarySlice = createSlice({
  name: 'dictionary',
  initialState,
  reducers: {
    open(
      state,
      { payload }: PayloadAction<DictionaryTree | DictionaryCategory>
    ) {
      const id = payload.id;
      state.all[id] = { ...payload, outputs: null };
      state.order.push(id);
    },
    close(
      state,
      {
        payload: { id },
      }: PayloadAction<{ id: UniqueId; reason?: DictionaryCloseReason }>
    ) {
      delete state.all[id];
      state.order.splice(state.order.indexOf(id), 1);
    },
    submit: emptyAction<{ id: UniqueId; value?: any }>(),
    updateOutputs: pendingAction<DictionaryState, { id: UniqueId }>(),
    updateOutputsSuccess: successAction<
      DictionaryState,
      { id: UniqueId; outputs: DictionaryOutputs | null }
    >((state, { payload: { id, outputs } }) => {
      state.all[id].outputs = outputs;
    }),
    updateOutputsFailure: failureAction(),
  },
});

export const dictionaryReducer = dictionarySlice.reducer;

export default dictionarySlice.actions;
